import { useEffect, useCallback, useRef } from 'react';
import axios, { AxiosResponse, CancelTokenSource } from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { handleApiError } from './handle-api-error';
import { serviceURLs } from './axios.constant';

interface IUseApiData {
  fetchData: <T>(
    url: string,
    notify: (message: string, status: string) => void,
    baseService: string,
    params?: any,
    contentType?: string,
    tmsApplicationCarrier?: string
  ) => Promise<AxiosResponse<T> | null>;
}

const useGetApiData = (): IUseApiData => {
  const { getAccessTokenSilently } = useAuth0();
  const controllerRef = useRef<AbortController>();
  const sourceRef = useRef<CancelTokenSource>();

  const fetchData = useCallback(
    async <T>(
      url: string,
      notify: (message: string, status: string) => void,
      baseService: string,
      params?: any,
      contentType?: string,
      tmsApplicationCarrier?: string
    ): Promise<AxiosResponse<T> | null> => {
      try {
        const baseUrl = baseService
          ? window.gbl_service_urls[serviceURLs[baseService]]
          : window.gbl_service_urls['tendering_service_URL'];
        const apiUrl = `${baseUrl}${url}`;
        const accessToken = await getAccessTokenSilently();
        const selectedLanguage = localStorage.getItem('selectedLanguage');

        const response: AxiosResponse<T> = await axios.get<T>(apiUrl, {
          signal: controllerRef.current?.signal,
          cancelToken: sourceRef.current?.token,
          headers: {
            'Content-Type': contentType || 'application/json',
            'Authorization': `Bearer ${accessToken}`,
            'Accept-Language': selectedLanguage,
            'Accept': contentType || 'application/json',
            'TMS-App-Identifier': tmsApplicationCarrier || 'application/json'
          },
          params: params
        });

        return response;
      } catch (error: any) {
        const logObject = [
          {
            error_message: error.message,
            error_name: error.name,
            stack_trace: error.stack,
            request_method: error.config?.method,
            request_url: error.config?.url,
            status_code: error.response?.status,
            module: baseService
          }
        ];
        // Log error to console since useLogger doesn't exist
        // console.error('API Error:', JSON.stringify(logObject));
        if (axios.isCancel(error)) {
          // eslint-disable-next-line no-console
          console.log('Request canceled:', error.message);
        } else {
          handleApiError(error, notify);
        }
        return null;
      }
    },
    // eslint-disable-next-line
    [getAccessTokenSilently]
  );

  useEffect(() => {
    // Create new instances for this component lifecycle
    //  controllerRef.current = new AbortController();
    //  sourceRef.current = axios.CancelToken.source();

    // Clean up the effect
    return () => {
      if (controllerRef.current) {
        controllerRef.current.abort();
      }
      if (sourceRef.current) {
        sourceRef.current.cancel('Request cancelled due to component unmount');
      }
    };
    // eslint-disable-next-line
  }, []);

  return { fetchData };
};

export default useGetApiData;
